import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { usePersistedClientId } from "@/lib/clientSelection";
import { formatMissingMonthsPhrase } from "@/lib/formatMissingMonths";
import XmlPendingDetailsList from "@/components/confronta/XmlPendingDetailsList";

/**
 * Aviso compacto en Confronta: metadatos disponibles pero XML aún pendiente.
 */
export default function ConfrontaXmlPendingBanner() {
  const [clients, setClients] = useState([]);
  const [clientId] = usePersistedClientId(clients);
  const [pendingCount, setPendingCount] = useState(0);
  const [missingMonths, setMissingMonths] = useState([]);
  const [pendingDetails, setPendingDetails] = useState([]);
  const [recoveryInProgress, setRecoveryInProgress] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/clients");
        if (!cancelled) setClients(data || []);
      } catch {
        if (!cancelled) setClients([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!clientId) {
      setPendingCount(0);
      setMissingMonths([]);
      setPendingDetails([]);
      setRecoveryInProgress(false);
      return;
    }
    setLoading(true);
    try {
      const { data: stats } = await api.get("/sat/cfdi/stats", {
        params: { client_id: clientId },
      });
      const pending = Math.max(0, Number(stats?.xml_pending_integration) || 0);
      setPendingCount(pending);
      setRecoveryInProgress(Boolean(stats?.sync_in_progress));
      if (pending <= 0) {
        setMissingMonths([]);
        setPendingDetails([]);
        return;
      }
      const { data: probe } = await api.get("/sat/cfdi/audit/status", {
        params: { client_id: clientId },
      });
      setPendingCount(Math.max(0, Number(probe?.xml_pending_integration) || pending));
      setMissingMonths(Array.isArray(probe?.missing_months) ? probe.missing_months : []);
      setPendingDetails(Array.isArray(probe?.pending_details) ? probe.pending_details : []);
      setRecoveryInProgress(
        Boolean(stats?.sync_in_progress || probe?.status === "running"),
      );
    } catch (err) {
      setPendingCount(0);
      setMissingMonths([]);
      setPendingDetails([]);
      if (import.meta.env?.MODE === "development") {
        console.warn(formatApiError(err, "No se pudo consultar XML pendiente"));
      }
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    refresh();
    if (!clientId) return undefined;
    const id = window.setInterval(refresh, 60000);
    return () => window.clearInterval(id);
  }, [clientId, refresh]);

  if (!clientId || loading) return null;
  if (pendingCount <= 0) return null;

  const monthsPhrase = formatMissingMonthsPhrase(missingMonths);

  return (
    <div
      className={`rounded-sm border px-4 py-3 text-sm leading-relaxed flex gap-3 mb-4 ${
        recoveryInProgress ? "border-sky-200 bg-sky-50 text-sky-950" : "border-amber-200 bg-amber-50 text-amber-950"
      }`}
      role="note"
      data-testid="confronta-xml-pending-banner"
    >
      {recoveryInProgress ? (
        <Loader2 className="w-5 h-5 shrink-0 animate-spin mt-0.5" aria-hidden />
      ) : (
        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" aria-hidden />
      )}
      <div className="min-w-0">
        <p className="font-semibold m-0">
          {recoveryInProgress ? "Recuperación de XML en curso" : "Algunos XML aún no están integrados"}
        </p>
        <p className="m-0 mt-1 text-xs">
          Hay{" "}
          <strong className="tabular-nums">{pendingCount.toLocaleString("es-MX")}</strong> comprobante
          {pendingCount === 1 ? "" : "s"} sin archivo XML en su copia local.
          {recoveryInProgress ? (
            <> El sistema ya está en proceso de recuperación.</>
          ) : (
            <>
              {" "}
              La confronta usa <strong>metadatos</strong> (montos, UUID, fechas); algunos cruces de detalle
              pueden quedar incompletos hasta que termine la integración.
            </>
          )}
          {monthsPhrase ? (
            <>
              {" "}
              <span className="font-medium">Periodos:</span> <strong>{monthsPhrase}</strong>.
            </>
          ) : null}
        </p>
        {pendingDetails.length > 0 ? (
          <div className="mt-3">
            <p className="text-[11px] font-semibold m-0 mb-1.5">Comprobantes sin XML ({pendingDetails.length})</p>
            <XmlPendingDetailsList items={pendingDetails} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
